import { useState, useMemo, Fragment } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  X,
  Shield,
  Check,
  Search,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { governanceService } from "@/services/governance.service";
import { tenantService } from "@/services/tenant.service";
import type { RoleItem, PermissionItem } from "@/types";
import toast from "react-hot-toast";

interface RoleFormModalProps {
  role?: RoleItem | null;
  onClose: () => void;
}

export function RoleFormModal({ role, onClose }: RoleFormModalProps) {
  const queryClient = useQueryClient();
  const isEditing = Boolean(role);

  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [tenantId, setTenantId] = useState<string>(role?.tenantId ?? "");
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(
    new Set(role?.permissions ?? []),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const { data: tenants } = useQuery({
    queryKey: ["admin-tenants", "role-modal"],
    queryFn: () => tenantService.list({ page: 1, limit: 100 }),
  });

  const { data: permissions } = useQuery({
    queryKey: ["admin-permissions"],
    queryFn: () => governanceService.listPermissions(),
  });

  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, PermissionItem[]> = {};
    (permissions ?? []).forEach((perm) => {
      const group = perm.group || "General";
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(perm);
    });
    return grouped;
  }, [permissions]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedPermissions;

    const result: Record<string, PermissionItem[]> = {};
    Object.entries(groupedPermissions).forEach(([group, perms]) => {
      const filtered = perms.filter(
        (p) =>
          p.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      if (filtered.length > 0) {
        result[group] = filtered;
      }
    });
    return result;
  }, [groupedPermissions, searchQuery]);

  const { mutateAsync: createRole, isPending: isCreating } = useMutation({
    mutationFn: (payload: {
      name: string;
      description?: string;
      tenantId?: string | null;
      permissions?: string[];
    }) => governanceService.createRole(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      toast.success("Role created successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to create role");
    },
  });

  const { mutateAsync: updateRole, isPending: isUpdating } = useMutation({
    mutationFn: (payload: {
      name: string;
      description?: string;
      permissions?: string[];
    }) => governanceService.updateRole(role!.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      toast.success("Role updated successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update role");
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Role name is required");
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      tenantId: tenantId || null,
      permissions: Array.from(selectedPermissions),
    };

    if (isEditing) {
      await updateRole(payload);
    } else {
      await createRole(payload);
    }
  };

  const togglePermission = (key: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  const selectAllInGroup = (group: string) => {
    const groupPerms = groupedPermissions[group] ?? [];
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      groupPerms.forEach((p) => next.add(p.key));
      return next;
    });
  };

  const deselectAllInGroup = (group: string) => {
    const groupPerms = groupedPermissions[group] ?? [];
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      groupPerms.forEach((p) => next.delete(p.key));
      return next;
    });
  };

  const isGroupFullySelected = (group: string) => {
    const groupPerms = groupedPermissions[group] ?? [];
    return groupPerms.every((p) => selectedPermissions.has(p.key));
  };

  const isGroupPartiallySelected = (group: string) => {
    const groupPerms = groupedPermissions[group] ?? [];
    const selectedCount = groupPerms.filter((p) =>
      selectedPermissions.has(p.key),
    ).length;
    return selectedCount > 0 && selectedCount < groupPerms.length;
  };

  const isPending = isCreating || isUpdating;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-blue-50 text-blue-600 border border-blue-100">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">
                {isEditing ? "Edit Role" : "Create New Role"}
              </h2>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                {isEditing
                  ? "Modify role configuration and permissions"
                  : "Define a new role with specific permissions"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-hidden flex flex-col"
        >
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* Basic Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Role Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., STORE_MANAGER"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none transition-colors"
                  disabled={role?.isSystem}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Tenant Scope
                </label>
                <select
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  disabled={isEditing}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 bg-white focus:border-blue-500 focus:outline-none transition-colors disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">Global (All Tenants)</option>
                  {tenants?.data.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this role's responsibilities..."
                rows={2}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none transition-colors resize-none"
              />
            </div>

            {/* Permissions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-black text-slate-900">
                    Permissions
                  </h3>
                  <p className="text-[10px] text-slate-500 font-medium mt-0.5">
                    {selectedPermissions.size} of {permissions?.length ?? 0}{" "}
                    selected
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-xl">
                  <Search className="w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="bg-transparent border-none outline-none text-xs text-slate-900 placeholder-slate-400 w-32"
                  />
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl overflow-hidden max-h-[300px] overflow-y-auto">
                {Object.entries(filteredGroups).map(([group, perms]) => {
                  const isExpanded = expandedGroups.has(group);
                  const isFullySelected = isGroupFullySelected(group);
                  const isPartiallySelected = isGroupPartiallySelected(group);

                  return (
                    <Fragment key={group}>
                      <div
                        className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => toggleGroup(group)}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                          <span className="text-xs font-black uppercase tracking-widest text-slate-600">
                            {group}
                          </span>
                          <span className="text-[10px] font-bold text-slate-400">
                            ({perms.length})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isFullySelected) {
                              deselectAllInGroup(group);
                            } else {
                              selectAllInGroup(group);
                            }
                          }}
                          className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest transition-all ${
                            isFullySelected
                              ? "bg-blue-600 text-white"
                              : isPartiallySelected
                                ? "bg-blue-100 text-blue-600"
                                : "bg-slate-200 text-slate-500 hover:bg-slate-300"
                          }`}
                        >
                          {isFullySelected ? "All" : "Select All"}
                        </button>
                      </div>
                      {isExpanded &&
                        perms.map((perm) => {
                          const isSelected = selectedPermissions.has(perm.key);
                          return (
                            <div
                              key={perm.id}
                              onClick={() => togglePermission(perm.key)}
                              className={`flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-none cursor-pointer transition-colors ${
                                isSelected
                                  ? "bg-blue-50/50"
                                  : "hover:bg-slate-50"
                              }`}
                            >
                              <div className="flex items-center gap-3 pl-7">
                                <div
                                  className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all ${
                                    isSelected
                                      ? "bg-blue-600 border-blue-600"
                                      : "border-slate-300"
                                  }`}
                                >
                                  {isSelected && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <div>
                                  <code className="text-[11px] font-bold text-slate-900">
                                    {perm.key}
                                  </code>
                                  {perm.description && (
                                    <p className="text-[10px] text-slate-500 mt-0.5">
                                      {perm.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </Fragment>
                  );
                })}
                {Object.keys(filteredGroups).length === 0 && (
                  <div className="py-8 text-center text-sm text-slate-400">
                    No permissions found
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-8 py-4 border-t border-slate-100 bg-slate-50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-200 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || role?.isSystem}
              className="px-6 py-2.5 rounded-xl bg-blue-600 text-white text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
            >
              {isPending
                ? "Saving..."
                : isEditing
                  ? "Update Role"
                  : "Create Role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
